/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useState, useEffect } from "react";

/**
 * Crea un contexto para el estado de la pantalla.
 * @type {React.Context<ScreenContextType>}
 */
export const ScreenContext = createContext();

/**
 * Proveedor del contexto `ScreenContext` que gestiona el estado de las dimensiones de la pantalla.
 * @param {Object} props - Propiedades del componente.
 * @param {React.ReactNode} props.children - Componentes hijos que serán envueltos por el proveedor.
 * @returns {JSX.Element} Proveedor del contexto que envuelve a sus hijos.
 */
export const ScreenProvider = React.memo(({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [isTabletVertical, setIsTabletVertical] = useState(
    window.innerWidth >= 600 &&
      window.innerWidth < 900 &&
      window.innerHeight >= window.innerWidth
  );
  const [isTabletHorizontal, setIsTabletHorizontal] = useState(
    window.innerWidth >= 900 &&
      window.innerWidth < 1200 &&
      window.innerHeight < window.innerWidth
  );
  const [isDesktop, setIsDesktop] = useState(
    window.innerWidth >= 1200 && window.innerWidth <= 1800
  );
  const [isXXL, setIsXXL] = useState(window.innerWidth >= 1800);
  const [dimensionHeight, setDimensionHeight] = useState();
  const [dimensionWidth, setDimensionWidth] = useState();

  /**
   * Maneja el evento de redimensionamiento de la ventana, actualizando los estados correspondientes.
   */
  const handleResize = () => {
    const newIsMobile = window.innerWidth < 600;
    const newIsTabletVertical =
      window.innerWidth >= 600 &&
      window.innerWidth < 900 &&
      window.innerHeight >= window.innerWidth;
    const newIsTabletHorizontal =
      window.innerWidth >= 900 &&
      window.innerWidth < 1200 &&
      window.innerHeight < window.innerWidth;
    const newIsDesktop = window.innerWidth >= 1200 && window.innerWidth <= 1800;
    const newIsXXL = window.innerWidth >= 1800;

    setDimensionHeight(window.innerHeight);
    setDimensionWidth(window.innerWidth);

    if (newIsMobile !== isMobile) setIsMobile(newIsMobile);
    if (newIsTabletVertical !== isTabletVertical)
      setIsTabletVertical(newIsTabletVertical);
    if (newIsTabletHorizontal !== isTabletHorizontal)
      setIsTabletHorizontal(newIsTabletHorizontal);
    if (newIsDesktop !== isDesktop) setIsDesktop(newIsDesktop);
    if (newIsXXL !== isXXL) setIsXXL(newIsXXL);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize(); // Llamar para configurar el estado inicial
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  return (
    <ScreenContext.Provider
      value={{
        isMobile,
        isTabletVertical,
        isTabletHorizontal,
        isDesktop,
        isXXL,
        dimensionHeight,
        dimensionWidth,
      }}
    >
      {children}
    </ScreenContext.Provider>
  );
});
